import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import {
  TerraDraw,
  TerraDrawModeUndoRedo,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  TerraDrawSessionUndoRedo,
  TerraDrawUndoRedoKeyboardShortcuts,
} from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'
import {
  useCallback, useEffect, useRef, useState,
} from 'react'

const POLYGON_COLOR = '#e49042'

function toApiPolygon(feature) {
  if (!feature || feature.geometry.type !== 'Polygon') return null

  return {
    type: 'polygon',
    coordinates: feature.geometry.coordinates,
  }
}

function toTerraDrawFeature(draw, polygon) {
  if (!polygon?.coordinates?.length) return null

  return {
    id: draw.getFeatureId(),
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: polygon.coordinates,
    },
    properties: {
      mode: 'polygon',
    },
  }
}

function currentPolygon(draw) {
  return draw.getSnapshot().find((feature) => (
    feature.geometry.type === 'Polygon'
      && feature.properties.currentlyDrawing !== true
  ))
}

export function useTerraDraw({ enabled, polygon, onChangePolygon }) {
  const map = useMap()
  const maps = useMapsLibrary('maps')
  const onChangePolygonRef = useRef(onChangePolygon)
  const [draw, setDraw] = useState(null)
  const [history, setHistory] = useState({ canUndo: false, canRedo: false })

  useEffect(() => {
    onChangePolygonRef.current = onChangePolygon
  }, [onChangePolygon])

  useEffect(() => {
    if (!enabled || !map || !maps) return undefined

    const mapElement = map.getDiv()
    if (!mapElement.id) mapElement.id = 'region-polygon-map-canvas'

    const terraDraw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: window.google.maps,
        map,
        coordinatePrecision: 9,
      }),
      modes: [
        new TerraDrawPolygonMode({
          styles: {
            fillColor: POLYGON_COLOR,
            fillOpacity: 0.5,
            outlineColor: POLYGON_COLOR,
          },
        }),
        new TerraDrawSelectMode({
          flags: {
            polygon: {
              feature: {
                draggable: true,
                coordinates: {
                  midpoints: true,
                  draggable: true,
                  deletable: true,
                },
              },
            },
          },
          styles: {
            selectedPolygonColor: POLYGON_COLOR,
            selectedPolygonFillOpacity: 0.5,
            selectedPolygonOutlineColor: POLYGON_COLOR,
          },
        }),
      ],
      undoRedo: {
        modeLevel: new TerraDrawModeUndoRedo({ maxStackSize: 100 }),
        sessionLevel: new TerraDrawSessionUndoRedo({ maxStackSize: 100 }),
        keyboardShortcuts: new TerraDrawUndoRedoKeyboardShortcuts(),
      },
    })

    const updatePolygon = () => {
      onChangePolygonRef.current(toApiPolygon(currentPolygon(terraDraw)))
    }

    const updateHistory = () => {
      setHistory({
        canUndo: terraDraw.canUndo(),
        canRedo: terraDraw.canRedo(),
      })
    }

    const handleFinish = (finishedId) => {
      const finishedPolygon = terraDraw.getSnapshotFeature(finishedId)
      const previousPolygons = terraDraw.getSnapshot()
        .filter((feature) => (
          feature.geometry.type === 'Polygon' && feature.id !== finishedId
        ))

      if (finishedPolygon && previousPolygons.length) {
        const [polygonToUpdate, ...duplicatePolygons] = previousPolygons
        const idsToRemove = [
          finishedId,
          ...duplicatePolygons.map((feature) => feature.id),
        ]

        terraDraw.removeFeatures(idsToRemove)
        terraDraw.clearUndoRedoHistory()
        terraDraw.updateFeatureGeometry(
          polygonToUpdate.id,
          finishedPolygon.geometry,
        )
        terraDraw.selectFeature(polygonToUpdate.id)
      } else {
        terraDraw.selectFeature(finishedId)
      }

      updateHistory()
      updatePolygon()
    }

    const handleReady = () => {
      const feature = toTerraDrawFeature(terraDraw, polygon)

      if (feature) {
        const [validation] = terraDraw.addFeatures([feature])

        if (!validation.valid) {
          throw new Error(validation.reason || 'Polígono da região inválido')
        }

        terraDraw.selectFeature(feature.id)
        updatePolygon()
      } else {
        terraDraw.setMode('polygon')
      }

      terraDraw.clearUndoRedoHistory()
      updateHistory()
      setDraw(terraDraw)
    }

    terraDraw.on('ready', handleReady)
    terraDraw.on('finish', handleFinish)
    terraDraw.on('change', updatePolygon)
    terraDraw.on('history', updateHistory)
    terraDraw.start()

    return () => {
      terraDraw.off('ready', handleReady)
      terraDraw.off('finish', handleFinish)
      terraDraw.off('change', updatePolygon)
      terraDraw.off('history', updateHistory)
      terraDraw.clear()
      terraDraw.stop()
      setDraw(null)
    }
  }, [enabled, map, maps, polygon])

  const startPolygon = useCallback(() => {
    if (draw) draw.setMode('polygon')
  }, [draw])

  const undo = useCallback(() => {
    if (draw) draw.undo()
  }, [draw])

  const redo = useCallback(() => {
    if (draw) draw.redo()
  }, [draw])

  return {
    ready: Boolean(draw),
    canUndo: Boolean(draw) && history.canUndo,
    canRedo: Boolean(draw) && history.canRedo,
    startPolygon,
    undo,
    redo,
  }
}
